import { useId, useState, type FormEvent } from "react";
import { useCreateProviderCredential } from "../../api/queries";
import type { ProviderKind } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface AddProviderCredentialModalProps {
  open: boolean;
  fleetId: string;
  onClose: () => void;
}

// Hetzner is the only provider wired up server-side today
// (provisioning.KindHetzner) — a plain field rather than a dropdown until
// there's a second option to choose between.
const PROVIDER: ProviderKind = "hetzner";

export function AddProviderCredentialModal({ open, fleetId, onClose }: AddProviderCredentialModalProps) {
  const labelId = useId();
  const tokenId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateProviderCredential(fleetId);
  const [label, setLabel] = useState("");
  const [token, setToken] = useState("");

  function handleClose() {
    setLabel("");
    setToken("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedLabel = label.trim();
    mutation.mutate(
      { kind: PROVIDER, label: submittedLabel, token },
      {
        onSuccess: (created) => {
          pushToast({ title: `Credential "${created.label}" added.`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't add "${submittedLabel}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    // Handed off to the mutation above — there's no reason to keep the
    // token in component state a moment longer, success or not (S-13).
    setToken("");
  }

  return (
    <Modal open={open} title="Add a provider credential" onClose={handleClose} initialFocusId={labelId}>
      <form onSubmit={handleSubmit} noValidate>
        <p className="form-note">
          Comebye uses this token only to provision and manage compute on your Hetzner project — it's sealed on
          write and never shown again.
        </p>

        <label className="form-field" htmlFor={labelId}>
          <span>Label</span>
          <input
            id={labelId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="Hetzner — prod project"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={100}
          />
        </label>

        <label className="form-field" htmlFor={tokenId}>
          <span>Hetzner API token</span>
          <input
            id={tokenId}
            type="password"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            required
          />
          <span className="form-field__hint">
            Sealed on write. Never shown again — add a new credential later if you need to replace it.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !label.trim() || !token}>
            {mutation.isPending ? "Adding…" : "Add credential"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
